/**
 * The Árvore tab's org chart (Figma `320:11540`): levels as left-to-right columns of
 * connector-joined nodes, selection driving `TreePath`'s `Caminho` bar. A node's "contas"
 * figure is `subtree_accounts` -- the same number the old accordion and `TeamsList`
 * already show -- since neither reach nor a platform icon has a producer in `TeamOut`.
 *
 * Every node carries its own `TeamActions` menu beside its selection button -- a sibling, since a
 * menu trigger cannot nest inside a button -- so a team at any level is acted on without first
 * being selected into the `Caminho` bar.
 *
 * Mounted inside Equipes (`embedded`), the card draws no title of its own -- the tab
 * already carries one. Rendered on its own with a bare `campaignId` it keeps its
 * `CardTitle`, the same way it always has.
 */
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Users } from 'lucide-react';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { LoadingState, QueryStates } from '../../../chrome/state';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Card, CardContent, CardTitle } from '../../../ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '../../../ui/empty';
import { cn } from '../../../ui/utils';
import { t } from '../i18n';
import { TeamActions } from './TeamActions';
import { TreePath } from './TreePath';
import { useStructure } from './hooks';
import type { LevelOut, StructureOut, TeamOut } from './schemas';

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part !== '');
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

type Column = { level: LevelOut; teams: TeamOut[] };

function buildColumns(structure: StructureOut): Column[] {
  return [...structure.levels]
    .sort((a, b) => a.position - b.position)
    .map((level) => ({
      level,
      teams: structure.teams
        .filter((team) => team.level_id === level.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    }));
}

function ancestryOf(team: TeamOut, byId: Map<string, TeamOut>): TeamOut[] {
  const chain: TeamOut[] = [];
  let current: TeamOut | undefined = team;
  while (current !== undefined) {
    chain.unshift(current);
    current = current.parent_team_id !== null ? byId.get(current.parent_team_id) : undefined;
  }
  return chain;
}

/** Cycled by column index. `Consumption.tsx` and `NcsSeries.tsx` paint the same tokens
 *  this way -- as CSS var strings, not `bg-chart-N` classes -- because a Tailwind class
 *  built from a template literal never appears in the source text the scanner reads. */
const LEVEL_ACCENTS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

function levelAccent(index: number): string {
  return LEVEL_ACCENTS[index % LEVEL_ACCENTS.length]!;
}

/** A horizontal-elbow line from a parent's right edge to a child's left edge, in the
 *  container's own coordinate space. */
function connectorPath(container: DOMRect, parent: DOMRect, child: DOMRect): string {
  const startX = parent.right - container.left;
  const startY = parent.top + parent.height / 2 - container.top;
  const endX = child.left - container.left;
  const endY = child.top + child.height / 2 - container.top;
  const midX = (startX + endX) / 2;
  return `M ${startX} ${startY} H ${midX} V ${endY} H ${endX}`;
}

type TreeChartProps = {
  structure: StructureOut;
  campaignId?: string;
};

function TreeChart({ structure, campaignId }: TreeChartProps): ReactNode {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const columns = useMemo(() => buildColumns(structure), [structure]);
  const teamsById = useMemo(
    () => new Map(structure.teams.map((team) => [team.id, team])),
    [structure.teams],
  );
  const selectedTeam = selectedId !== null ? (teamsById.get(selectedId) ?? null) : null;
  const path = selectedTeam !== null ? ancestryOf(selectedTeam, teamsById) : [];

  const parentPairs = useMemo(
    () =>
      structure.teams
        .filter((team) => team.parent_team_id !== null)
        .map((team) => [team.parent_team_id!, team.id] as const),
    [structure.teams],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLButtonElement>());
  const [connectorPaths, setConnectorPaths] = useState<string[]>([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    function recompute() {
      const containerEl = containerRef.current;
      if (containerEl === null) return;
      const containerRect = containerEl.getBoundingClientRect();
      const next: string[] = [];
      for (const [parentId, childId] of parentPairs) {
        const parentEl = nodeRefs.current.get(parentId);
        const childEl = nodeRefs.current.get(childId);
        if (parentEl === undefined || childEl === undefined) continue;
        next.push(
          connectorPath(
            containerRect,
            parentEl.getBoundingClientRect(),
            childEl.getBoundingClientRect(),
          ),
        );
      }
      setConnectorPaths(next);
    }

    recompute();
    // jsdom has no `ResizeObserver` and lays nothing out -- the overlay stays empty under
    // test, same reasoning as `useOverflows`. A real browser still gets a live redraw.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [parentPairs, columns]);

  return (
    <div data-slot="tree" className="flex flex-col">
      <TreePath campaignId={campaignId} path={path} onClear={() => setSelectedId(null)} />
      {/* `min-w-0`: a flex item's default min-width is `auto`, which grows this row to fit
       *  every column rather than letting `overflow-x-auto` clip and scroll it -- the
       *  classic flexbox overflow trap, and how this leaked into the page's own scrollbar. */}
      <div ref={containerRef} className="relative flex min-w-0 gap-8 overflow-x-auto p-4">
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 size-full"
          data-slot="tree-connectors"
        >
          {connectorPaths.map((d, index) => (
            <path key={index} d={d} fill="none" stroke="var(--color-border)" strokeWidth={1.5} />
          ))}
        </svg>
        {columns.map((column, columnIndex) => (
          <div
            key={column.level.id}
            role="group"
            aria-label={column.level.name}
            className="relative flex w-65 shrink-0 flex-col gap-3"
          >
            <div className="flex items-center gap-2 pl-1 text-eyebrow text-foreground">
              <span
                aria-hidden
                className="size-2.25 shrink-0 rounded-full"
                style={{ backgroundColor: levelAccent(columnIndex) }}
              />
              <span>{column.level.name}</span>
              <span className="text-caption font-normal text-muted-foreground">
                {integer(column.teams.length, getLocale())}
              </span>
            </div>
            {column.teams.map((team) => {
              const selected = team.id === selectedId;
              return (
                <div key={team.id} className="relative">
                  <button
                    type="button"
                    ref={(el) => {
                      if (el) {
                        nodeRefs.current.set(team.id, el);
                      } else {
                        nodeRefs.current.delete(team.id);
                      }
                    }}
                    data-slot="tree-node"
                    data-team-id={team.id}
                    aria-pressed={selected}
                    onClick={() => setSelectedId(selected ? null : team.id)}
                    className={cn(
                      'relative flex w-full flex-col gap-1 rounded-lg border py-3 pr-12 pl-4 text-left outline-none transition-colors',
                      'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                      selected
                        ? 'border-primary bg-primary-quiet'
                        : 'border-border bg-card hover:bg-accent',
                    )}
                  >
                    <span
                      aria-hidden
                      className="absolute inset-y-0 left-0 w-1 rounded-l-lg"
                      style={{ backgroundColor: levelAccent(columnIndex) }}
                    />
                    <span className="flex items-center gap-2">
                      <span className="flex-1 text-title-xs text-foreground">{team.name}</span>
                      <span className="flex items-center gap-1 text-label-sm text-foreground">
                        <Users aria-hidden className="size-3" />
                        {t('teams.badges.subtree', { count: team.subtree_accounts })}
                      </span>
                    </span>
                    {team.lead_name !== null && (
                      <span className="flex items-center gap-1.5 text-caption text-muted-foreground">
                        <Avatar size="sm">
                          <AvatarFallback>{initialsOf(team.lead_name)}</AvatarFallback>
                        </Avatar>
                        {team.lead_name}
                      </span>
                    )}
                  </button>
                  {campaignId !== undefined && (
                    <div className="absolute top-2 right-2">
                      <TeamActions campaignId={campaignId} team={team} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function isStructureEmpty(data: StructureOut): boolean {
  return data.levels.length === 0 && data.teams.length === 0;
}

function NoTeams(): ReactNode {
  return (
    <Empty data-slot="empty-structure">
      <EmptyHeader>
        <EmptyTitle>{t('teams.empty.title')}</EmptyTitle>
        <EmptyDescription>{t('teams.empty.description')}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function renderStructure(data: StructureOut, campaignId: string | undefined): ReactNode {
  return isStructureEmpty(data) ? (
    <NoTeams />
  ) : (
    <TreeChart structure={data} campaignId={campaignId} />
  );
}

export type TreeProps = {
  structure?: StructureOut;
  campaignId?: string;
  /** True when a parent already carries this chart's own title (the Árvore tab inside
   *  Equipes); false draws `CardTitle`, the way a standalone mount always has. */
  embedded?: boolean;
};

export function Tree({
  structure: passedStructure,
  campaignId,
  embedded = false,
}: TreeProps): ReactNode {
  const query = useStructure(campaignId ?? '');

  const body =
    passedStructure !== undefined ? (
      renderStructure(passedStructure, campaignId)
    ) : (
      <QueryStates
        query={query}
        isEmpty={isStructureEmpty}
        empty={<NoTeams />}
        loading={
          <div className="p-6">
            <LoadingState />
          </div>
        }
      >
        {(data) => <TreeChart structure={data} campaignId={campaignId} />}
      </QueryStates>
    );

  return (
    <Card data-slot="tree-card" className="gap-0 overflow-hidden py-0">
      {!embedded && (
        <div className="border-b px-4 py-4">
          <CardTitle className="text-title-sm">{t('teams.title')}</CardTitle>
        </div>
      )}
      <CardContent className="px-0 py-0">{body}</CardContent>
    </Card>
  );
}
